import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Search, Download, Printer, Upload, LayoutGrid, List, Settings, CheckSquare, X, Filter, GitMerge, MoreHorizontal, Sparkles, Loader2, MessageSquare, Users } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import KanbanBoard from '@/components/KanbanBoard';
import { safePrint } from '@/lib/safePrint';

import { STATUS_OPTIONS, CATEGORY_OPTIONS, emptyForm } from '@/components/leads/constants';
import { LeadsFilters } from '@/components/leads/LeadsFilters';
import { LeadsTable } from '@/components/leads/LeadsTable';
import { LeadsMobileCards } from '@/components/leads/LeadsMobileCards';
import { LeadFormDialog, FieldManagerDialog } from '@/components/leads/LeadFormDialog';
import { ShareDialog } from '@/components/leads/LeadShareDialog';
import { MergeLeadDialog } from '@/components/leads/MergeLeadDialog';
import { BulkMergeDialog } from '@/components/leads/BulkMergeDialog';
import { BulkSmsDialog } from '@/components/leads/BulkSmsDialog';
import { HunterDomainSearchDialog } from '@/components/leads/HunterDomainSearchDialog';
import { useTour } from '@/contexts/TourContext';
import { LEADS_TOUR } from '@/lib/tourDefinitions';
import { PullToRefresh } from '@/components/PullToRefresh';
import logger from '@/lib/logger';

export default function LeadsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [filterCategory, setFilterCategory] = useState(searchParams.get('category') || '');
  const [filterSource, setFilterSource] = useState('');
  const [filterScore, setFilterScore] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterSharedWithMe, setFilterSharedWithMe] = useState(searchParams.get('shared') === 'true');
  const [filterMinEmployees, setFilterMinEmployees] = useState('');
  const [filterMaxEmployees, setFilterMaxEmployees] = useState('');
  const [filterMinRevenue, setFilterMinRevenue] = useState('');
  const [filterMaxRevenue, setFilterMaxRevenue] = useState('');
  const [filterVerifiedEmail, setFilterVerifiedEmail] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [showSaveFilter, setShowSaveFilter] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showShare, setShowShare] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [form, setForm] = useState({ ...emptyForm });
  const [editLead, setEditLead] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [sortBy, setSortBy] = useState('date');
  const [customFields, setCustomFields] = useState([]);
  const [showFieldMgr, setShowFieldMgr] = useState(false);
  const [newField, setNewField] = useState({ name: '', field_type: 'text', options: '', required: false });
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkAction, setShowBulkAction] = useState(false);
  const [showBulkSms, setShowBulkSms] = useState(false);
  const [showHunterDomain, setShowHunterDomain] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkCategory, setBulkCategory] = useState('');
  const [showBulkShare, setShowBulkShare] = useState(false);
  const [bulkShareMembers, setBulkShareMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [shareTab, setShareTab] = useState('members');
  const [shareNote, setShareNote] = useState('');
  const [bulkShareTab, setBulkShareTab] = useState('members');
  const [bulkShareNote, setBulkShareNote] = useState('');
  const [bulkSelectedGroups, setBulkSelectedGroups] = useState([]);
  const [duplicates, setDuplicates] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [showBulkMerge, setShowBulkMerge] = useState(false);
  const [bulkClusters, setBulkClusters] = useState([]);
  const [bulkScanning, setBulkScanning] = useState(false);
  const { startTour, loaded: tourLoaded, isTourCompleted } = useTour();

  const fetchLeads = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (filterStatus) params.status = filterStatus;
    if (filterCategory) params.category = filterCategory;
    if (filterSource) params.lead_source = filterSource;
    if (filterScore) params.score_label = filterScore;
    if (filterDateFrom) params.date_from = filterDateFrom;
    if (filterDateTo) params.date_to = filterDateTo;
    if (filterTag) params.tag = filterTag;
    if (filterSharedWithMe) params.shared_with_me = true;
    if (filterMinEmployees) params.min_employees = parseInt(filterMinEmployees, 10);
    if (filterMaxEmployees) params.max_employees = parseInt(filterMaxEmployees, 10);
    if (filterMinRevenue) params.min_revenue = parseFloat(filterMinRevenue);
    if (filterMaxRevenue) params.max_revenue = parseFloat(filterMaxRevenue);
    if (filterVerifiedEmail) params.verified_email = filterVerifiedEmail;
    params.page = currentPage;
    params.limit = 50;
    api.getLeads(params).then(r => {
      const d = r.data;
      setLeads(Array.isArray(d) ? d : d.leads || []);
      setTotalLeads(d.total || 0);
      setTotalPages(d.pages || 1);
    }).catch(() => toast.error('Failed to load leads')).finally(() => setLoading(false));
  }, [search, filterStatus, filterCategory, filterSource, filterScore, filterDateFrom, filterDateTo, filterTag, filterSharedWithMe, filterMinEmployees, filterMaxEmployees, filterMinRevenue, filterMaxRevenue, filterVerifiedEmail, currentPage]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [search, filterStatus, filterCategory, filterSource, filterScore, filterDateFrom, filterDateTo, filterTag, filterSharedWithMe, filterMinEmployees, filterMaxEmployees, filterMinRevenue, filterMaxRevenue, filterVerifiedEmail]);
  useEffect(() => {
    if (tourLoaded && !loading && leads.length >= 0 && !isTourCompleted('leads')) {
      const timer = setTimeout(() => startTour('leads', LEADS_TOUR.steps), 1200);
      return () => clearTimeout(timer);
    }
  }, [tourLoaded, loading, leads.length, isTourCompleted, startTour]);
  const fetchSavedFilters = useCallback(() => { api.getSavedFilters().then(r => setSavedFilters(r.data)).catch(err => logger.error(err)); }, []);
  useEffect(() => { fetchSavedFilters(); }, [fetchSavedFilters]);
  useEffect(() => { api.getCustomFields().then(r => setCustomFields(r.data)).catch(err => logger.error(err)); }, []);

  const doCreateLead = async () => {
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [], business_start_date: form.business_start_date || null };
      await api.createLead(payload);
      toast.success('Lead created');
      setShowCreate(false); setForm({ ...emptyForm }); setDuplicates(null); fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create lead'); }
  };

  const handleCheckDuplicates = async () => {
    if (!form.name.trim()) { toast.error('Lead name is required'); return; }
    setDuplicateLoading(true);
    try {
      const { data } = await api.checkDuplicates({ name: form.name, email: form.email, phone: form.phone, company: form.company });
      if (data.count > 0) {
        setDuplicates(data.duplicates);
      } else {
        await doCreateLead();
      }
    } catch (err) {
      // If check fails, proceed with creation
      logger.error('Duplicate check failed:', err);
      await doCreateLead();
    }
    setDuplicateLoading(false);
  };

  const handleCreate = async () => { await doCreateLead(); };
  const handleDismissDuplicates = () => { setDuplicates(null); };
  const handleCreateAnyway = async () => {
    setDuplicateLoading(true);
    await doCreateLead();
    setDuplicateLoading(false);
  };
  const handleMergeDuplicate = (existingLead) => {
    setMergeTarget(existingLead);
  };
  const handleMergeComplete = (mergedLead) => {
    setMergeTarget(null);
    setShowCreate(false);
    setForm({ ...emptyForm });
    setDuplicates(null);
    toast.success('Leads merged');
    fetchLeads();
  };

  const handleScanDuplicates = async () => {
    setShowBulkMerge(true);
    setBulkScanning(true);
    setBulkClusters([]);
    try {
      const { data } = await api.scanDuplicates();
      setBulkClusters(data.clusters);
      if (data.total_clusters === 0) toast.info('No duplicates found — your database is clean!');
      else toast.success(`Found ${data.total_clusters} duplicate cluster${data.total_clusters > 1 ? 's' : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to scan duplicates');
    }
    setBulkScanning(false);
  };

  const handleUpdate = async () => {
    if (!editLead) return;
    try {
      const payload = { name: form.name, email: form.email, phone: form.phone, company: form.company, address: form.address, city: form.city, state: form.state, zip_code: form.zip_code, status: form.status, category: form.category, sub_category: form.sub_category, lead_source: form.lead_source, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [], custom_fields: form.custom_fields, number_of_employees: form.number_of_employees, annual_revenue: form.annual_revenue, website: form.website, business_start_date: form.business_start_date || null };
      await api.updateLead(editLead.id, payload);
      toast.success('Lead updated'); setEditLead(null); fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try { await api.deleteLead(id); toast.success('Lead deleted'); fetchLeads(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const openShare = async (lead) => {
    setShowShare(lead); setSelectedMembers([]); setSelectedGroups([]); setShareTab('members');
    try { const r = await api.getMembers(); setMembers(r.data.filter(m => m.id !== user?.id && m.id !== user?._id)); } catch (err) { logger.error(err); }
    try { const r = await api.getGroups(); setGroups(r.data); } catch (err) { logger.error(err); }
  };

  const handleShare = async () => {
    if (!showShare || (selectedMembers.length === 0 && selectedGroups.length === 0)) return;
    try {
      if (selectedMembers.length > 0) await api.shareLead(showShare.id, selectedMembers, shareNote);
      for (const gid of selectedGroups) await api.shareLeadsToGroup(gid, [showShare.id]);
      toast.success('Lead shared'); setShowShare(null); setShareNote(''); fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to share'); }
  };

  const openEdit = (lead) => {
    setEditLead(lead);
    setForm({ name: lead.name, email: lead.email || '', phone: lead.phone || '', company: lead.company || '', address: lead.address || '', city: lead.city || '', state: lead.state || '', zip_code: lead.zip_code || '', status: lead.status, category: lead.category || '', sub_category: lead.sub_category || '', lead_source: lead.lead_source || '', tags: lead.tags?.join(', ') || '', notes: '', custom_fields: lead.custom_fields || {}, number_of_employees: lead.number_of_employees ?? null, annual_revenue: lead.annual_revenue ?? null, website: lead.website || '', business_start_date: lead.business_start_date || '' });
  };

  const handleExport = async (exportAll) => {
    try {
      const params = exportAll ? { export_all: true } : {};
      if (!exportAll) { if (search) params.search = search; if (filterStatus) params.status = filterStatus; if (filterCategory) params.category = filterCategory; }
      const response = await api.exportLeadsCSV(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `leads_export_${new Date().toISOString().slice(0, 10)}.csv`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(exportAll ? 'All leads exported' : 'Filtered leads exported');
    } catch (err) { logger.error(err); toast.error('Failed to export leads'); }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await api.importLeadsCSV(file);
      let msg = `Imported ${data.imported} leads`;
      if (data.duplicates_found > 0) msg += ` (${data.duplicates_found} potential duplicates flagged)`;
      toast.success(msg);
      if (data.errors?.length > 0) toast.warning(`${data.errors.length} warnings during import`, { description: data.errors.slice(0, 3).join('\n') });
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    e.target.value = '';
  };

  const handlePrint = () => {
    const printData = leads.length > 0 ? leads : [];
    const html = `<html><head><title>Leads - Real Leads CRM</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'IBM Plex Sans',Arial,sans-serif;padding:32px;color:#0F172A}h1{font-size:20px;font-weight:700;margin-bottom:4px}.sub{font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:.15em;margin-bottom:20px}table{width:100%;border-collapse:collapse;font-size:12px}th{text-align:left;padding:8px 10px;border-bottom:2px solid #0F172A;font-weight:600;font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:#64748B}td{padding:7px 10px;border-bottom:1px solid #E2E8F0}tr:nth-child(even){background:#F8FAFC}.footer{margin-top:24px;font-size:10px;color:#94A3B8;text-align:right}@media print{body{padding:16px}}</style></head><body><h1>Leads Report</h1><p class="sub">Real Leads CRM &middot; ${new Date().toLocaleDateString()} &middot; ${printData.length} leads</p><table><thead><tr><th>Name</th><th>Company</th><th>Email</th><th>Phone</th><th>Status</th><th>Category</th><th>Owner</th></tr></thead><tbody>${printData.map(l => `<tr><td><strong>${l.name||''}</strong></td><td>${l.company||'---'}</td><td>${l.email||'---'}</td><td>${l.phone||'---'}</td><td>${l.status||''}</td><td>${l.category||'---'}</td><td>${l.created_by_name||'---'}</td></tr>`).join('')}</tbody></table><p class="footer">Printed from Real Leads CRM</p></body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    safePrint(html);
    win.close();
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === leads.length ? [] : leads.map(l => l.id));
  const handleBulkUpdate = async () => {
    if (selectedIds.length === 0) return;
    const data = { lead_ids: selectedIds };
    if (bulkStatus) data.status = bulkStatus;
    if (bulkCategory) data.category = bulkCategory;
    try { await api.bulkUpdateLeads(data); toast.success(`Updated ${selectedIds.length} leads`); setSelectedIds([]); setShowBulkAction(false); setBulkStatus(''); setBulkCategory(''); fetchLeads(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Bulk update failed'); }
  };
  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} leads?`)) return;
    try { await api.bulkDeleteLeads(selectedIds); toast.success(`Deleted ${selectedIds.length} leads`); setSelectedIds([]); fetchLeads(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Bulk delete failed'); }
  };
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkHuntering, setBulkHuntering] = useState(false);
  const handleBulkEnrich = async () => {
    if (selectedIds.length === 0 || bulkEnriching) return;
    if (selectedIds.length > 50) { toast.error('Bulk enrich is limited to 50 leads at a time'); return; }
    if (!window.confirm(`Enrich ${selectedIds.length} leads with Seamless.AI? Missing fields (title, industry, SIC/NAICS, etc.) will be filled in.`)) return;
    setBulkEnriching(true);
    try {
      const { data } = await api.bulkEnrichSeamless(selectedIds);
      const parts = [];
      if (data.enriched) parts.push(`${data.enriched} enriched`);
      if (data.no_match) parts.push(`${data.no_match} no match`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      if (data.failed) parts.push(`${data.failed} failed`);
      toast.success(`Bulk enrich complete: ${parts.join(' · ')}`);
      setSelectedIds([]);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk enrichment failed');
    } finally { setBulkEnriching(false); }
  };
  const handleBulkHunter = async () => {
    if (selectedIds.length === 0 || bulkHuntering) return;
    if (selectedIds.length > 50) { toast.error('Hunter bulk find is limited to 50 leads at a time'); return; }
    if (!window.confirm(`Use Hunter.io to find missing emails on ${selectedIds.length} leads? Each successful match uses 1 Hunter credit.`)) return;
    setBulkHuntering(true);
    try {
      const { data } = await api.hunterBulkFind(selectedIds);
      const parts = [];
      if (data.found) parts.push(`${data.found} found`);
      if (data.saved) parts.push(`${data.saved} saved`);
      if (data.no_match) parts.push(`${data.no_match} no match`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      if (data.failed) parts.push(`${data.failed} failed`);
      toast.success(`Hunter bulk find: ${parts.join(' · ')}`);
      setSelectedIds([]);
      fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Hunter bulk find failed');
    } finally { setBulkHuntering(false); }
  };
  const openBulkShare = async () => {
    setBulkShareMembers([]); setBulkSelectedGroups([]); setBulkShareTab('members'); setShowBulkShare(true);
    try { const r = await api.getMembers(); setMembers(r.data.filter(m => m.id !== user?.id && m.id !== user?._id)); } catch (err) { logger.error(err); }
    try { const r = await api.getGroups(); setGroups(r.data); } catch (err) { logger.error(err); }
  };
  const handleBulkShare = async () => {
    if (bulkShareMembers.length === 0 && bulkSelectedGroups.length === 0) return;
    try {
      if (bulkShareMembers.length > 0) await api.bulkShareLeads(selectedIds, bulkShareMembers);
      for (const gid of bulkSelectedGroups) await api.shareLeadsToGroup(gid, selectedIds);
      toast.success('Leads shared'); setShowBulkShare(false); setSelectedIds([]); fetchLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAddField = async () => {
    if (!newField.name.trim()) { toast.error('Field name required'); return; }
    try {
      const payload = { ...newField, options: newField.field_type === 'select' ? newField.options.split(',').map(o => o.trim()).filter(Boolean) : [] };
      await api.createCustomField(payload); toast.success('Custom field added');
      setNewField({ name: '', field_type: 'text', options: '', required: false });
      const r = await api.getCustomFields(); setCustomFields(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteField = async (id) => {
    try { await api.deleteCustomField(id); toast.success('Field removed'); const r = await api.getCustomFields(); setCustomFields(r.data); } catch (err) { logger.error(err); toast.error('Failed'); }
  };

  const isViewer = user?.role === 'viewer';
  const isAdmin = ['admin','super_admin'].includes(user?.role);
  const isRegularAdmin = user?.role === 'admin';
  const setCF = (key, val) => setForm(prev => ({ ...prev, custom_fields: { ...prev.custom_fields, [key]: val } }));
  const sortedLeads = [...leads].sort((a, b) => sortBy === 'score' ? (b.score?.total || 0) - (a.score?.total || 0) : 0);
  const activeFilterCount = [filterStatus, filterCategory, filterSource, filterScore, filterDateFrom, filterDateTo, filterTag, filterSharedWithMe, filterMinEmployees, filterMaxEmployees, filterMinRevenue, filterMaxRevenue, filterVerifiedEmail].filter(Boolean).length;

  const handleSaveFilter = async () => {
    if (!filterName.trim()) { toast.error('Enter a name for this filter'); return; }
    const filters = {};
    if (filterStatus) filters.status = filterStatus; if (filterCategory) filters.category = filterCategory;
    if (filterSource) filters.lead_source = filterSource; if (filterScore) filters.score_label = filterScore;
    if (filterDateFrom) filters.date_from = filterDateFrom; if (filterDateTo) filters.date_to = filterDateTo;
    if (filterTag) filters.tag = filterTag; if (filterSharedWithMe) filters.shared_with_me = true;
    if (filterVerifiedEmail) filters.verified_email = filterVerifiedEmail;
    if (search) filters.search = search;
    try { await api.createSavedFilter({ name: filterName, filters }); toast.success('Filter saved'); setFilterName(''); setShowSaveFilter(false); fetchSavedFilters(); }
    catch { toast.error('Failed to save'); }
  };
  const applyFilter = (sf) => {
    const f = sf.filters || {};
    setFilterStatus(f.status || ''); setFilterCategory(f.category || ''); setFilterSource(f.lead_source || '');
    setFilterScore(f.score_label || ''); setFilterDateFrom(f.date_from || ''); setFilterDateTo(f.date_to || '');
    setFilterTag(f.tag || ''); setFilterSharedWithMe(!!f.shared_with_me); setSearch(f.search || '');
    setFilterVerifiedEmail(f.verified_email || '');
    if (f.lead_source || f.score_label || f.date_from || f.date_to || f.tag || f.shared_with_me || f.verified_email) setShowAdvanced(true);
    toast.success(`Applied: ${sf.name}`);
  };
  const clearAllFilters = () => { setSearch(''); setFilterStatus(''); setFilterCategory(''); setFilterSource(''); setFilterScore(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterTag(''); setFilterSharedWithMe(false); setFilterMinEmployees(''); setFilterMaxEmployees(''); setFilterMinRevenue(''); setFilterMaxRevenue(''); setFilterVerifiedEmail(''); };
  const handleDeleteSavedFilter = async (id) => { try { await api.deleteSavedFilter(id); fetchSavedFilters(); toast.success('Filter removed'); } catch (err) { logger.error(err); } };

  return (
    <PullToRefresh onRefresh={fetchLeads}>
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-1 hidden sm:block">Leads</p>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Lead Management</h1>
          {isRegularAdmin && (
            <div
              className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-sm bg-[#EFF6FF] dark:bg-blue-950/40 border border-[#0047AB]/15 dark:border-blue-900/40 text-[11px] text-[#0047AB] dark:text-blue-300 font-medium"
              data-testid="admin-scope-chip"
              title="You see leads you created, leads shared with you, and leads shared into your groups. Super admins see everything."
            >
              <Users className="w-3 h-3" /> Showing leads in your groups
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="rounded-sm text-xs sm:text-sm h-8 sm:h-9" onClick={handlePrint} data-testid="print-leads-btn"><Printer className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline"> Print</span></Button>
          {!isViewer && (
            <>
              <label className="inline-flex items-center gap-1 px-2 sm:px-3 py-1.5 border border-slate-200 rounded-sm text-xs sm:text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer transition-all h-8 sm:h-9" data-testid="import-csv-btn">
                <Upload className="w-3.5 h-3.5" /><span className="hidden sm:inline"> Import</span>
                <input type="file" accept=".csv" onChange={handleImportCSV} className="hidden" data-testid="import-csv-input" />
              </label>
              <Link to="/import/crm" className="hidden lg:inline-flex items-center gap-1 px-3 py-1.5 border border-[#0047AB]/20 bg-[#EFF6FF] rounded-sm text-sm font-medium text-[#0047AB] hover:bg-blue-100 transition-all h-9" data-testid="crm-import-btn">
                <Upload className="w-4 h-4" /> CRM Import
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" className="rounded-sm text-xs sm:text-sm h-8 sm:h-9" data-testid="export-leads-btn"><Download className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline"> Export</span></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport(false)} data-testid="export-filtered-btn"><Download className="w-4 h-4 mr-2" /> Export Filtered</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport(true)} data-testid="export-all-btn"><Download className="w-4 h-4 mr-2" /> Export All</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {isAdmin && <Button variant="outline" className="rounded-sm text-xs sm:text-sm h-8 sm:h-9 hidden md:inline-flex" onClick={() => setShowFieldMgr(true)} data-testid="manage-fields-btn"><Settings className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline"> Fields</span></Button>}
              {isAdmin && <Button variant="outline" className="rounded-sm text-xs sm:text-sm h-8 sm:h-9 hidden md:inline-flex border-[#0047AB]/20 text-[#0047AB] hover:bg-blue-50" onClick={handleScanDuplicates} data-testid="scan-duplicates-btn"><GitMerge className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline"> Scan Duplicates</span></Button>}
              {isAdmin && <Button variant="outline" className="rounded-sm text-xs sm:text-sm h-8 sm:h-9 hidden md:inline-flex border-[#FF7E0E]/30 text-[#FF7E0E] hover:bg-[#FF7E0E]/5" onClick={() => setShowHunterDomain(true)} data-testid="hunter-domain-launcher"><Search className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline"> Hunter Search</span></Button>}
              {/* Mobile-only "More" overflow with CRM Import / Fields / Scan Duplicates */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="lg:hidden">
                  <Button variant="outline" className="rounded-sm text-xs sm:text-sm h-8 sm:h-9 px-2" data-testid="leads-more-menu">
                    <MoreHorizontal className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate('/import/crm')} data-testid="more-crm-import">
                    <Upload className="w-4 h-4 mr-2" /> CRM Import
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => setShowFieldMgr(true)} data-testid="more-fields">
                      <Settings className="w-4 h-4 mr-2" /> Manage Fields
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem onClick={handleScanDuplicates} data-testid="more-scan-dupes">
                      <GitMerge className="w-4 h-4 mr-2" /> Scan Duplicates
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => setShowHunterDomain(true)} data-testid="more-hunter-search">
                      <Search className="w-4 h-4 mr-2" /> Hunter Search
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={() => { setShowCreate(true); setForm({ ...emptyForm }); }} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs sm:text-sm h-8 sm:h-9" data-testid="add-lead-btn">
                <Plus className="w-3.5 h-3.5 sm:mr-1" /><span className="hidden sm:inline"> Add Lead</span><span className="sm:hidden"> Add</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Search + Filters */}
      <div className="space-y-3 mb-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search leads..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-sm text-sm" data-testid="search-leads-input" />
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterStatus} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[130px] rounded-sm text-xs sm:text-sm h-9" data-testid="filter-status"><Filter className="w-3 h-3 mr-1 text-slate-400" /><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Status</SelectItem>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={v => setFilterCategory(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[130px] rounded-sm text-xs sm:text-sm h-9 hidden sm:flex" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <LeadsFilters
              filterSource={filterSource} setFilterSource={setFilterSource}
              filterScore={filterScore} setFilterScore={setFilterScore}
              filterDateFrom={filterDateFrom} setFilterDateFrom={setFilterDateFrom}
              filterDateTo={filterDateTo} setFilterDateTo={setFilterDateTo}
              filterTag={filterTag} setFilterTag={setFilterTag}
              filterSharedWithMe={filterSharedWithMe} setFilterSharedWithMe={setFilterSharedWithMe}
              filterMinEmployees={filterMinEmployees} setFilterMinEmployees={setFilterMinEmployees}
              filterMaxEmployees={filterMaxEmployees} setFilterMaxEmployees={setFilterMaxEmployees}
              filterMinRevenue={filterMinRevenue} setFilterMinRevenue={setFilterMinRevenue}
              filterMaxRevenue={filterMaxRevenue} setFilterMaxRevenue={setFilterMaxRevenue}
              filterVerifiedEmail={filterVerifiedEmail} setFilterVerifiedEmail={setFilterVerifiedEmail}
              activeFilterCount={activeFilterCount} clearAllFilters={clearAllFilters} leads={leads}
              savedFilters={savedFilters} applyFilter={applyFilter} handleDeleteSavedFilter={handleDeleteSavedFilter}
              showSaveFilter={showSaveFilter} setShowSaveFilter={setShowSaveFilter}
              filterName={filterName} setFilterName={setFilterName} handleSaveFilter={handleSaveFilter}
              showAdvanced={showAdvanced} setShowAdvanced={setShowAdvanced}
            />
            <div className="hidden sm:block ml-auto">
              <Tabs value={viewMode} onValueChange={setViewMode}>
                <TabsList className="h-8 p-0.5 bg-slate-100 rounded-sm" data-testid="view-mode-toggle">
                  <TabsTrigger value="table" className="h-7 px-2 text-xs rounded-sm data-[state=active]:bg-white" data-testid="view-table"><List className="w-3.5 h-3.5 mr-1" /> Table</TabsTrigger>
                  <TabsTrigger value="kanban" className="h-7 px-2 text-xs rounded-sm data-[state=active]:bg-white" data-testid="view-kanban"><LayoutGrid className="w-3.5 h-3.5 mr-1" /> Kanban</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && viewMode === 'table' && (
        <div className="mb-4 p-3 bg-[#EFF6FF] border border-[#0047AB]/20 rounded-sm flex items-center justify-between" data-testid="bulk-action-bar">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-4 h-4 text-[#0047AB]" />
            <span className="text-sm font-medium text-[#0047AB]">{selectedIds.length} selected</span>
            <button onClick={() => setSelectedIds([])} className="text-xs text-slate-500 hover:text-slate-700"><X className="w-3 h-3" /></button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs rounded-sm" onClick={() => setShowBulkAction(true)} data-testid="bulk-update-btn">Update Status</Button>
            <Button variant="outline" size="sm" className="h-7 text-xs rounded-sm" onClick={openBulkShare} data-testid="bulk-share-btn">Share</Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs rounded-sm border-[#0047AB]/30 text-[#0047AB] hover:bg-[#EFF6FF]"
              onClick={() => setShowBulkSms(true)}
              data-testid="bulk-sms-btn"
              title="Send SMS/MMS to all selected leads via ClickSend"
            >
              <MessageSquare className="w-3 h-3 mr-1" /> SMS
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs rounded-sm border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-60"
              onClick={handleBulkEnrich}
              disabled={bulkEnriching}
              data-testid="bulk-enrich-btn"
              title="Fill missing title, department, industry, SIC/NAICS via Seamless.AI"
            >
              {bulkEnriching ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
              {bulkEnriching ? 'Enriching…' : 'Enrich'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs rounded-sm border-[#FF7E0E]/40 text-[#FF7E0E] hover:bg-[#FF7E0E]/5 disabled:opacity-60"
              onClick={handleBulkHunter}
              disabled={bulkHuntering}
              data-testid="bulk-hunter-btn"
              title="Find missing emails via Hunter.io using name + website/company (1 credit per match)"
            >
              {bulkHuntering ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
              {bulkHuntering ? 'Finding…' : 'Find Emails'}
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs rounded-sm border-red-200 text-red-600 hover:bg-red-50" onClick={handleBulkDelete} data-testid="bulk-delete-btn">Delete</Button>
          </div>
        </div>
      )}

      {/* Views */}
      {viewMode === 'kanban' ? (
        loading ? <div className="flex items-center justify-center py-20"><div className="animate-spin w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full" /></div>
        : <KanbanBoard leads={leads} onRefresh={fetchLeads} onEdit={openEdit} onShare={openShare} isViewer={isViewer} />
      ) : (
        <>
          <LeadsMobileCards loading={loading} sortedLeads={sortedLeads} isViewer={isViewer} openEdit={openEdit} openShare={openShare} handleDelete={handleDelete} onArchived={fetchLeads} />
          <LeadsTable leads={leads} loading={loading} sortedLeads={sortedLeads} isViewer={isViewer} selectedIds={selectedIds} toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll} sortBy={sortBy} setSortBy={setSortBy} openEdit={openEdit} openShare={openShare} handleDelete={handleDelete} />
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && viewMode !== 'kanban' && (
        <div className="flex items-center justify-between mt-4 px-1" data-testid="leads-pagination">
          <p className="text-xs text-slate-500">{totalLeads} lead{totalLeads !== 1 ? 's' : ''} total</p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 text-xs rounded-sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="pagination-prev">Prev</Button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pg;
              if (totalPages <= 7) pg = i + 1;
              else if (currentPage <= 4) pg = i + 1;
              else if (currentPage >= totalPages - 3) pg = totalPages - 6 + i;
              else pg = currentPage - 3 + i;
              return (
                <Button key={pg} variant={pg === currentPage ? 'default' : 'outline'} size="sm"
                  className={`h-7 w-7 text-xs rounded-sm p-0 ${pg === currentPage ? 'bg-slate-900 text-white' : ''}`}
                  onClick={() => setCurrentPage(pg)} data-testid={`pagination-page-${pg}`}
                >{pg}</Button>
              );
            })}
            <Button variant="outline" size="sm" className="h-7 text-xs rounded-sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} data-testid="pagination-next">Next</Button>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <LeadFormDialog
        open={showCreate || !!editLead} onClose={() => { setShowCreate(false); setEditLead(null); setDuplicates(null); }}
        form={form} setForm={setForm} editLead={editLead}
        handleCreate={handleCreate} handleUpdate={handleUpdate}
        customFields={customFields} setCF={setCF}
        duplicates={duplicates} duplicateLoading={duplicateLoading}
        onCheckDuplicates={handleCheckDuplicates}
        onDismissDuplicates={handleDismissDuplicates}
        onCreateAnyway={handleCreateAnyway}
        onMergeDuplicate={handleMergeDuplicate}
      />
      <MergeLeadDialog
        open={!!mergeTarget}
        onClose={() => setMergeTarget(null)}
        newLeadData={form}
        existingLead={mergeTarget}
        onMergeComplete={handleMergeComplete}
      />
      <ShareDialog
        show={showShare} onClose={() => { setShowShare(null); setShareNote(''); }} leadName={showShare?.name}
        members={members} groups={groups}
        selectedMembers={selectedMembers} setSelectedMembers={setSelectedMembers}
        selectedGroups={selectedGroups} setSelectedGroups={setSelectedGroups}
        shareTab={shareTab} setShareTab={setShareTab} onShare={handleShare}
        shareNote={shareNote} setShareNote={setShareNote}
      />
      <ShareDialog
        show={showBulkShare} onClose={() => { setShowBulkShare(false); setBulkShareNote(''); }} leadName={`${selectedIds.length} Leads`}
        members={members} groups={groups}
        selectedMembers={bulkShareMembers} setSelectedMembers={setBulkShareMembers}
        selectedGroups={bulkSelectedGroups} setSelectedGroups={setBulkSelectedGroups}
        shareTab={bulkShareTab} setShareTab={setBulkShareTab} onShare={handleBulkShare}
        shareNote={bulkShareNote} setShareNote={setBulkShareNote}
      />

      {/* Bulk Update Dialog */}
      {showBulkAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowBulkAction(false)}>
          <div className="bg-white rounded-sm p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-testid="bulk-update-dialog">
            <h3 className="font-heading font-semibold text-slate-900 mb-4">Bulk Update {selectedIds.length} Leads</h3>
            <div className="space-y-3 mb-4">
              <Select value={bulkStatus || "none"} onValueChange={v => setBulkStatus(v === 'none' ? '' : v)}>
                <SelectTrigger className="rounded-sm" data-testid="bulk-status-select"><SelectValue placeholder="Set Status" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No change</SelectItem>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={bulkCategory || "none"} onValueChange={v => setBulkCategory(v === 'none' ? '' : v)}>
                <SelectTrigger className="rounded-sm" data-testid="bulk-category-select"><SelectValue placeholder="Set Category" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No change</SelectItem>{CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowBulkAction(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={handleBulkUpdate} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="bulk-update-submit">Update All</Button>
            </div>
          </div>
        </div>
      )}

      <FieldManagerDialog open={showFieldMgr} onClose={() => setShowFieldMgr(false)} customFields={customFields} newField={newField} setNewField={setNewField} handleAddField={handleAddField} handleDeleteField={handleDeleteField} />
      <BulkMergeDialog
        open={showBulkMerge}
        onClose={() => setShowBulkMerge(false)}
        clusters={bulkClusters}
        scanning={bulkScanning}
        onRefresh={fetchLeads}
      />
      <BulkSmsDialog
        open={showBulkSms}
        onClose={() => setShowBulkSms(false)}
        leadIds={selectedIds}
        onSent={() => setSelectedIds([])}
      />
      <HunterDomainSearchDialog
        open={showHunterDomain}
        onClose={() => setShowHunterDomain(false)}
        onCreated={fetchLeads}
      />
    </div>
    </PullToRefresh>
  );
}
